import { ArrowUpRight, Mountain, Bed, Utensils, Leaf } from "lucide-react";
import { useTranslation } from "react-i18next";
import { APUSENI_TOURS, APUSENI_BG } from "../data/experiences";
import { HOLIDAYS_PATH, HOLIDAY_IMAGES } from "../data/holidays";
import { formatPrice } from "../i18n/pricing";
import { LocalizedLink } from "../lib/i18nRouting";
import SEO from "../components/site/SEO";
import Reveal from "../components/Reveal";

const PILLARS = [
  { icon: Mountain, k: "trails" },
  { icon: Bed, k: "lodging" },
  { icon: Utensils, k: "meals" },
  { icon: Leaf, k: "wellness" },
];

export default function ApuseniPage() {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;
  return (
    <div className="bg-bone min-h-screen" data-testid="apuseni-page">
      <SEO titleKey="apuseni.title" descKey="apuseni.description" path="/apuseni" />

      <section className="relative bg-forest-deep text-bone min-h-[80svh] flex items-end pt-32 pb-16 overflow-hidden">
        <div className="absolute inset-0">
          <img src={APUSENI_BG} alt={t("apuseni_page.title_a")} className="w-full h-full object-cover opacity-50" />
          <div className="absolute inset-0 bg-gradient-to-b from-forest-deep/70 via-forest-deep/40 to-forest-deep" />
          <div className="absolute inset-0 grain" />
        </div>
        <div className="relative max-w-7xl mx-auto px-6 lg:px-12 w-full">
          <Reveal>
            <div className="text-xs uppercase tracking-wider-3 text-bone/70 mb-6 flex items-center gap-3">
              <span className="inline-block w-8 h-px bg-bone/60" />
              {t("apuseni_page.eyebrow")}
            </div>
            <h1 className="font-display text-6xl md:text-8xl lg:text-9xl leading-[0.98]">
              {t("apuseni_page.title_a")}<br/><span className="italic">{t("apuseni_page.title_b")}</span>
            </h1>
            <p className="mt-8 max-w-2xl text-bone/85 text-base md:text-lg leading-relaxed">{t("apuseni_page.intro")}</p>
          </Reveal>
        </div>
      </section>

      <section className="bg-bone py-24 lg:py-32">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <Reveal>
            <div className="text-xs uppercase tracking-wider-3 text-moss mb-6 flex items-center gap-3">
              <span className="inline-block w-8 h-px bg-moss" />
              {t("apuseni_page.pillars_eyebrow")}
            </div>
            <h2 className="font-display text-4xl md:text-5xl lg:text-6xl text-forest max-w-4xl leading-[1.04]">
              {t("apuseni_page.pillars_title")}
            </h2>
          </Reveal>

          <div className="mt-16 grid grid-cols-1 md:grid-cols-4 gap-px bg-bone-deep border border-bone-deep">
            {PILLARS.map((p, i) => (
              <Reveal key={p.k} delay={i * 100} className="bg-bone p-8 lg:p-10 min-h-[280px] flex flex-col group hover:bg-forest hover:text-bone transition-colors duration-700">
                <p.icon className="w-7 h-7 text-forest group-hover:text-bone stroke-[1.25]" />
                <h3 className="font-display text-3xl mt-8 text-forest group-hover:text-bone">{t(`apuseni_page.pillars.${p.k}.t`)}</h3>
                <p className="text-sm text-muted-foreground group-hover:text-bone/80 mt-4 leading-relaxed">{t(`apuseni_page.pillars.${p.k}.d`)}</p>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-bone-deep py-24 lg:py-32">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <Reveal className="mb-16">
            <div className="text-xs uppercase tracking-wider-3 text-terracotta mb-4">{t("apuseni_page.adventures_eyebrow")}</div>
            <h2 className="font-display text-5xl md:text-6xl text-forest leading-[1.04]">{t("apuseni_page.adventures_title")}</h2>
          </Reveal>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 lg:gap-8">
            {APUSENI_TOURS.map((exp, i) => {
              const price = formatPrice(exp.price, lang, exp.slug);
              const duration = t(`tours.${exp.slug}.duration`);
              const name = t(`tours.${exp.slug}.name`);
              const short = t(`tours.${exp.slug}.short`);
              return (
                <Reveal key={exp.slug} delay={i * 120}>
                  <LocalizedLink to={`/experiente/${exp.slug}`} data-testid={`apuseni-card-${exp.slug}`} className="block bg-white border border-bone-deep h-full group">
                    <div className="relative aspect-[4/5] overflow-hidden bg-stone-warm">
                      <img src={exp.image} alt={name} loading="lazy" decoding="async" className="absolute inset-0 w-full h-full object-cover cine-img" />
                      <div className="absolute top-4 left-4 bg-bone/95 text-forest text-[10px] uppercase tracking-wider-2 px-3 py-1">{duration}</div>
                      <div className="absolute top-4 right-4 bg-forest text-bone text-[10px] uppercase tracking-wider-2 px-3 py-1">{price} {t("common.per_person")}</div>
                    </div>
                    <div className="p-6 lg:p-8">
                      <h3 className="font-display text-2xl md:text-3xl text-forest leading-tight">{name}</h3>
                      <p className="text-sm text-muted-foreground mt-3 leading-relaxed">{short}</p>
                      <div className="mt-6 inline-flex items-center gap-2 text-xs uppercase tracking-wider-3 text-forest">
                        {t("common.learn_more")} <ArrowUpRight className="w-4 h-4" />
                      </div>
                    </div>
                  </LocalizedLink>
                </Reveal>
              );
            })}
          </div>

          <Reveal delay={150} className="mt-6 lg:mt-8">
            <LocalizedLink
              to={HOLIDAYS_PATH}
              data-testid="apuseni-holidays-card"
              className="group grid grid-cols-1 md:grid-cols-12 bg-forest-deep text-bone border border-forest-deep overflow-hidden"
            >
              <div className="relative md:col-span-5 aspect-[16/10] md:aspect-auto md:min-h-[320px] overflow-hidden bg-forest">
                <img src={HOLIDAY_IMAGES.hero} alt={t("apuseni_page.holidays_card.title")} loading="lazy" decoding="async" className="absolute inset-0 w-full h-full object-cover cine-img" />
                <div className="absolute top-4 left-4 bg-terracotta text-white text-[10px] uppercase tracking-wider-2 px-3 py-1">{t("apuseni_page.holidays_card.eyebrow")}</div>
              </div>
              <div className="md:col-span-7 p-8 lg:p-12 flex flex-col justify-center relative">
                <div className="absolute inset-0 grain pointer-events-none" />
                <div className="relative">
                  <div className="text-xs uppercase tracking-wider-3 text-bone/60">{t("apuseni_page.holidays_card.sub")}</div>
                  <h3 className="font-display text-4xl md:text-5xl lg:text-6xl leading-[1.02] mt-3">{t("apuseni_page.holidays_card.title")}</h3>
                  <p className="mt-5 text-sm md:text-base text-bone/80 leading-relaxed max-w-xl">{t("apuseni_page.holidays_card.text")}</p>
                  <span className="mt-8 inline-flex items-center gap-3 bg-bone text-forest px-7 py-4 text-xs uppercase tracking-wider-3 group-hover:bg-bone-deep transition-colors self-start">
                    {t("apuseni_page.holidays_card.cta")} <ArrowUpRight className="w-4 h-4" />
                  </span>
                </div>
              </div>
            </LocalizedLink>
          </Reveal>
        </div>
      </section>

      <section className="bg-forest text-bone py-24 lg:py-32 relative overflow-hidden">
        <div className="absolute inset-0 grain" />
        <div className="relative max-w-7xl mx-auto px-6 lg:px-12 flex flex-col md:flex-row md:items-center md:justify-between gap-10">
          <Reveal><h2 className="font-display text-5xl md:text-6xl leading-[1.04]">{t("apuseni_page.cta_title")}</h2></Reveal>
          <Reveal delay={150}>
            <LocalizedLink to="/rezervare" data-testid="apuseni-page-cta" className="btn-lift inline-flex items-center gap-3 bg-bone text-forest px-8 py-4 text-xs uppercase tracking-wider-3 hover:bg-bone-deep">
              {t("hero.cta_book")} <ArrowUpRight className="w-4 h-4" />
            </LocalizedLink>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
